import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  VStack,
  Spinner,
  HStack,
  Button,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Flex,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { BaseWidgetProps } from '../widgets';
import { WidgetWrapper } from '../shared/components/WidgetWrapper';
import { PaRejectionReasonsOptions, WidgetType } from '../shared/types';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { statisticsApi } from '@texas/api/endpoints/statisticsApi';
import { RejectionReasonsChart } from './RejectionReasonsChart';
import { StatisticsHeaderFilters } from '../shared/StatisticsHeaderFilters';
import {
  SearchableMenuInput,
  SearchableMenuOption,
} from '@texas/components/shared/search/SearchableMenu';
import { useWidgetStorage } from '../shared/useWidgetStorage';
import { Icons } from '@texas/components/shared/Icons';
import { useMetadata } from '@texas/hooks/metadata/useMetadata';

interface Props extends BaseWidgetProps {
  options?: PaRejectionReasonsOptions;
}

export function PaRejectionReasonsWidget({
  color,
  customTitle,
  widgetId,
  options,
}: Props) {
  const { t } = useTranslation();
  const {
    filters,
    updateSampleSize,
    updateCustomerId,
    updateBranchIds,
    updateSupplierId,
  } = useWidgetStorage(widgetId);

  const [supplierSearchInput, setSupplierSearchInput] = useState('');

  const {
    suppliersData: [suppliers, getSuppliers, loadingSuppliers],
  } = useMetadata();

  const { data, loading, refetch } = useApiResource(
    statisticsApi.getPaRejectionReasons,
  );

  const selectedCustomerId = options?.customerId ?? filters.customerId;
  const selectedBranchIds =
    options?.branchIds && options.branchIds.length > 0
      ? options.branchIds
      : filters.branchIds;
  const selectedSupplierId = options?.supplierId ?? filters.supplierId;

  useEffect(() => {
    getSuppliers();
  }, [getSuppliers]);

  useEffect(() => {
    refetch({
      sampleSize: filters.sampleSize,
      customerId: selectedCustomerId ?? undefined,
      branchIds: selectedBranchIds.length > 0 ? selectedBranchIds : undefined,
      supplierId: selectedSupplierId ?? undefined,
    });
  }, [
    filters.sampleSize,
    selectedCustomerId,
    selectedBranchIds,
    selectedSupplierId,
    refetch,
  ]);

  const supplierDisplayText = useMemo(() => {
    if (selectedSupplierId === null) return t('filter.allSuppliers');
    const supplier = suppliers.find((s) => s.id === selectedSupplierId);
    return supplier?.name ?? t('general.supplier');
  }, [selectedSupplierId, suppliers, t]);

  const filteredSuppliers = useMemo(() => {
    if (supplierSearchInput.length === 0) return [];
    return suppliers
      .filter(
        (s) =>
          s.name.toLowerCase().includes(supplierSearchInput.toLowerCase()) &&
          !s.archived,
      )
      .slice(0, 30);
  }, [suppliers, supplierSearchInput]);

  return (
    <WidgetWrapper
      widgetId={widgetId}
      loading={loading}
      customColor={color}
      type={WidgetType.PaRejectionReasons}
      style="default"
      header={customTitle ?? t('widgets.paRejectionReasons.title')}
      toolbox={
        <HStack spacing={2} overflowX="auto">
          <StatisticsHeaderFilters
            sampleSize={filters.sampleSize}
            onSampleSizeChange={updateSampleSize}
            selectedCustomerId={selectedCustomerId}
            onCustomerChange={updateCustomerId}
            selectedBranchIds={selectedBranchIds}
            onBranchChange={updateBranchIds}
            isCustomerLocked={!!options?.customerId}
            isBranchLocked={
              !!options?.branchIds && options.branchIds.length > 0
            }
          />
          {!options?.supplierId && (
            <Menu isLazy={true}>
              <MenuButton
                as={Button}
                size="sm"
                minW="fit-content"
                variant="ghost"
                rightIcon={<Icons.ChevronDown boxSize={4} />}
              >
                <Text fontSize="sm">{supplierDisplayText}</Text>
              </MenuButton>
              <Portal>
                <MenuList maxH="400px" overflowY="auto">
                  <SearchableMenuInput
                    placeholder={t('general.search')}
                    value={supplierSearchInput}
                    onChange={setSupplierSearchInput}
                  />
                  <MenuItem
                    onClick={() => {
                      updateSupplierId(null);
                      setSupplierSearchInput('');
                    }}
                    fontWeight={selectedSupplierId === null ? 'bold' : 'normal'}
                  >
                    {t('filter.all')}
                  </MenuItem>
                  <MenuDivider />
                  {loadingSuppliers ? (
                    <MenuItem isDisabled={true}>
                      {t('general.loading')}
                    </MenuItem>
                  ) : (
                    <Flex flexDir="column" pb={2} gap={1} alignItems="start">
                      {filteredSuppliers.map((supplier) => (
                        <SearchableMenuOption
                          key={supplier.id}
                          label={supplier.name}
                          isSelected={supplier.id === selectedSupplierId}
                          showSelectionIndicator={true}
                          onClick={() => updateSupplierId(supplier.id)}
                        />
                      ))}
                    </Flex>
                  )}
                </MenuList>
              </Portal>
            </Menu>
          )}
        </HStack>
      }
    >
      <VStack align="stretch" spacing={4} height="100%">
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flex={1}
          >
            <Spinner />
          </Box>
        ) : (
          <RejectionReasonsChart data={data?.reasons ?? []} />
        )}
      </VStack>
    </WidgetWrapper>
  );
}
